import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import CurrencyFormat from "react-currency-format";
import { projectStatusText } from "@components/SowHeader/utils/sowHeaderHelpers";
import { formatDate } from "@utils/helperFunctions";
import { causeLabel } from "../constants/causes";
import { isHttpUrl } from "../constants/url";
import type { ChangeOrder } from "../types";

export type FieldLabels = Record<string, string | undefined>;

interface BuildColumnsArgs {
  /** The account's field-labels, for status label overrides. */
  fieldLabels?: FieldLabels;
  /** The project's currency unit, e.g. "$". */
  currency: string;
}

const DASH = "—";

/** At most 2 decimals, trailing zeros trimmed: 12 -> "12", 12.333 -> "12.33". */
export const formatHours = (hours: number) =>
  hours.toLocaleString("en-US", { maximumFractionDigits: 2 });

export const statusText = (row: ChangeOrder, fieldLabels?: FieldLabels) =>
  row.referenceOnly || row.status === "reference_only"
    ? "Reference only"
    : projectStatusText(row.status, fieldLabels);

export const buildColumns = ({
  fieldLabels,
  currency,
}: BuildColumnsArgs): ColumnDef<ChangeOrder>[] => [
  { accessorKey: "label", header: "Number" },
  {
    id: "cause",
    header: "Cause",
    cell: ({ row }) => (
      <span title={row.original.causeNote || undefined}>
        {causeLabel(row.original.cause)}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => statusText(row.original, fieldLabels),
  },
  {
    id: "counts",
    header: "Counts",
    cell: ({ row }) =>
      row.original.counts ? (
        <span aria-label="Counts" title="Counts toward the project's totals">
          <FontAwesomeIcon icon={faCheck} />
        </span>
      ) : null,
  },
  {
    id: "hours",
    header: "Hours",
    cell: ({ row }) =>
      row.original.referenceOnly || row.original.hours === null
        ? DASH
        : `+${formatHours(row.original.hours)}`,
  },
  {
    id: "revenue",
    header: "Revenue",
    cell: ({ row }) =>
      row.original.referenceOnly || row.original.revenue === null ? (
        DASH
      ) : (
        <CurrencyFormat
          displayType="text"
          prefix={`+${currency}`}
          isNumericString
          thousandSeparator
          value={row.original.revenue.toFixed(2)}
        />
      ),
  },
  {
    id: "reference",
    header: "Reference",
    cell: ({ row }) => {
      const { externalReference, externalUrl } = row.original;
      if (!externalReference && !externalUrl) return DASH;
      // Only http(s) URLs become links; anything else is shown as text.
      if (!externalUrl || !isHttpUrl(externalUrl)) {
        return externalReference || externalUrl;
      }
      return (
        <a href={externalUrl} target="_blank" rel="noopener noreferrer">
          {externalReference || externalUrl}
        </a>
      );
    },
  },
  {
    id: "author",
    header: "Author / Date",
    cell: ({ row }) => (
      <>
        <div>{row.original.userName || DASH}</div>
        <small className="text-muted">
          {formatDate(row.original.createdAt)}
        </small>
      </>
    ),
  },
  {
    id: "project",
    header: "Project",
    cell: ({ row }) =>
      row.original.referenceOnly || !row.original.childProjectId ? null : (
        <a href={`/projects/${row.original.childProjectId}/project_editor`}>
          Open
        </a>
      ),
  },
];
